// Moved from `src/hooks/mutation/useMutateAssignClinicalTask.ts` (now a pure
// shim) with the D2 family (ARCFE-945).
//
// TOAST: PORT -> META (ARCFE-1082). The `onAssignError` port is GONE. The copy
// now travels with the mutation as `meta.errorToast` — inert data typed by
// `@heidi/api/meta`, rendered by the single `MutationCache` subscriber in
// `src/utils/reactQueryClient.ts`. This file still imports no `toast`, no
// design system and nothing app-side, so `/data` is headless by exactly the
// same measurement as before; what changed is that the delegate dissolved from
// a bodied wrapper to a pure `export { … } from '@heidi/tasks/data'` shim. The
// two react-intl ids move VERBATIM — see `clinicalTaskToasts.messages.ts`.
//
// TWO DELTAS THIS BUYS, both disclosed rather than discovered:
//   (a) ORDER — the cache-level handler runs BEFORE `options.onError`, so the
//       toast now fires before this hook's optimistic ROLLBACK and its
//       `logger.error` rather than after them. Nothing observable depends on
//       either ordering: the rollback is a synchronous cache write the toast
//       cannot see, and no test asserted the interleaving.
//   (b) REHYDRATION — `meta` is plain JSON and survives dehydration, whereas a
//       component-bound port callback did not. For THIS package that is
//       theoretical, not live: the app's `dehydrateOptions`
//       (`src/utils/reactQueryPersistConfig.ts`) sets only
//       `shouldDehydrateQuery`, leaving `shouldDehydrateMutation` at the
//       TanStack default of `isPaused`, so the only tasks mutation that can
//       ever be persisted is one fired while OFFLINE and still paused at
//       unload. Verified by reading the config, not assumed.
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { logger } from '@heidi/runtime/logger';
import { AssigneeType } from '@heidi/schema/ml-scribe';

import type {
  ClinicalTaskResponse,
  GetClinicalTasksResponse,
} from '../../contracts/types';
import { assignClinicalTask } from '../api/clinical-tasks';
import {
  cancelClinicalTaskQueries,
  invalidateClinicalTaskQueries,
  snapshotClinicalTaskQueries,
} from './clinicalTaskQueryCache';
import { CLINICAL_TASK_TOASTS } from './clinicalTaskToasts.messages';

export interface AssignClinicalTaskParams {
  taskId: string;
  assigneeId: string;
  assigneeName?: string | null;
  assigneeAvatarUrl?: string | null;
  assigneeType?: AssigneeType;
}

type AssignMutationContext = {
  previousSnapshots: [QueryKey, GetClinicalTasksResponse | undefined][];
};

export const useMutateAssignClinicalTask = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ClinicalTaskResponse,
    AxiosError,
    AssignClinicalTaskParams,
    AssignMutationContext
  >({
    mutationFn: async ({ taskId, assigneeId, assigneeType }) => {
      const { data } = await assignClinicalTask(taskId, assigneeId, assigneeType);
      return data;
    },
    onMutate: async ({
      taskId,
      assigneeId,
      assigneeName,
      assigneeAvatarUrl,
      assigneeType,
    }) => {
      await cancelClinicalTaskQueries(queryClient);

      const previousSnapshots = snapshotClinicalTaskQueries(queryClient);

      const applyUpdate = (old: GetClinicalTasksResponse | undefined) => {
        if (!old?.tasks) return old;
        return {
          ...old,
          tasks: old.tasks.map((task) =>
            task.id === taskId
              ? {
                  ...task,
                  assignee_id: assigneeType === AssigneeType.team ? null : assigneeId,
                  assignee: { id: assigneeId, type: assigneeType ?? AssigneeType.user },
                  assignee_name: assigneeName ?? null,
                  assignee_avatar_url: assigneeAvatarUrl ?? null,
                }
              : task
          ),
        };
      };

      for (const [queryKey] of previousSnapshots) {
        queryClient.setQueryData<GetClinicalTasksResponse>(queryKey, applyUpdate);
      }

      return { previousSnapshots };
    },
    onSettled: () => invalidateClinicalTaskQueries(queryClient),
    onError: (error, _variables, context) => {
      if (context?.previousSnapshots) {
        for (const [key, data] of context.previousSnapshots) {
          queryClient.setQueryData(key, data);
        }
      }
      logger.error('Error assigning clinical task:', error.response?.data);
    },
    meta: CLINICAL_TASK_TOASTS.assign,
  });
};
